// waitTimeActions.js
// Action to fetch wait time and capacity from API and set to store

// Hash map for caching officeId results with expiration
const waitTimeCache = {};
import { useRootStore } from '../store';

/**
 * Fetch wait time and capacity for a given office ID and set to store
 * @param {string|number} officeId - The office identifier
 * @returns {Promise<void>}
 */
export async function fetchWaitTimeAndCapacity(officeId) {
  const rootStore = useRootStore();
  // O(1) lookup for cached result with expiration
  const CACHE_EXPIRATION_MS = rootStore.mapIWTPollinginterval || 300000; // Default to 5 minutes if not set
  const cached = waitTimeCache[officeId];
  const now = Date.now();
  if (cached && cached.timestamp) {
    if (now - cached.timestamp < CACHE_EXPIRATION_MS) {
      return cached.data;
    } else {
      // Delete expired entry
      delete waitTimeCache[officeId];
    }
  }
  try {
    const apiUrl = `${rootStore.getWTCURL}/${officeId}`;
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Ocp-Apim-Subscription-Key': process.env.VUE_APP_WTC_KEY || '',
      },
    });
    let result;
    if (!response.ok) {
      let errorMessage = `Wait times are not available right now`;
      if (response.status === 404) {
        errorMessage = "Wait times are not available right now";
      }
      result = {
        waitTime: null,
        capacity: null,
        lastUpdated: null,
        error: true,
        errorMessage
      };
    } else {
      result = await response.json();
    }
    // Use lastUpdated from API if available, else fallback to Date.now()
    let cacheTimestamp = Date.now();
    if (result && result.lastUpdated) {
      // Try to parse ISO or 'YYYY-MM-DD HH:mm:ss' format
      const parsed = Date.parse(result.lastUpdated);
      if (!isNaN(parsed)) {
        cacheTimestamp = parsed;
      }
    }
    waitTimeCache[officeId] = {
      data: result,
      timestamp: cacheTimestamp
    };
    return result;
  } catch (error) {
    const fallback = {
      waitTime: null,
      capacity: null,
      lastUpdated: null,
      error: true,
      errorMessage: 'Wait times are not available right now'
    };
    waitTimeCache[officeId] = {
      data: fallback,
      timestamp: Date.now()
    };
    return fallback;
  }

}

export function waitTimeRange(waitTime) {
    const rootStore = useRootStore();
    const waitInterval  = rootStore.getIWTWaitIntervalTime || 20; // Default to 20 if not set
    // Show as "X-Ymin" where X is rounded to nearest 5, Y = X+waitInterval
    if (!waitTime) return '';
    const parts = typeof waitTime === 'string' ? waitTime.split(':') : [];
    if (parts.length !== 3) return waitTime;
    const [hh, mm, ss] = parts.map(Number);
    if (isNaN(hh) || isNaN(mm) || isNaN(ss)) return waitTime;
    let totalMin = hh * 60 + mm + (ss > 0 ? 1 : 0);
    // Round to nearest 5
    let rounded = Math.round(totalMin / 5) * 5;
    let upper = rounded + waitInterval;
    if (rounded < 0) rounded = 0;
    if (upper < 0) upper = 0;
    return `${rounded}-${upper} minutes`;
}
